import { useCallback } from "react";
import { useDispatch, useStore } from "react-redux";
import { RootState, useAppSelector } from "src/store";
import { useGroupsApi } from "../api";
import { resetError, resetList, setError, setFilter, setList, setLoading } from "../slices";

const getServerSortField = (sort?: string) => {
  if (!sort) return undefined;

  const sortMap: Record<string, string> = {
    validContactCount: "valid_contacts",
    createdAt: "date_created",
    updatedAt: "date_updated",
  };

  const [field, order] = sort.split("_");
  const serverField = sortMap[field] || field;
  return `${serverField}_${order}`;
};

export const useGroupsService = () => {
  const dispatch = useDispatch();
  const { getState } = useStore<RootState>();
  const { loading, filter, list, error } = useAppSelector((state) => state.groups);

  const api = useGroupsApi();

  const getGroups = useCallback(async (companyId: string) => {
    const filter = getState().groups.filter;

    try {
      dispatch(resetError());
      dispatch(setLoading({ getList: true }));

      const response = await api.getGroups({
        companyId,
        limit: filter.limit,
        offset: filter.offset,

        query: filter.query || undefined,
        sort: getServerSortField(filter.sort),
        statuses: filter.statuses,
      });
      dispatch(setList(response.data));

      return response.data;
    } catch (e) {
      dispatch(setError(e));
      throw e;
    } finally {
      dispatch(setLoading({ getList: false }));
    }
  }, []);

  const changeGroupsFilter = useCallback((filter: Partial<RootState["groups"]["filter"]>) => {
    dispatch(setFilter(filter));
  }, []);

  const resetGroups = useCallback(() => {
    dispatch(resetList());
  }, []);

  return {
    loading,
    filter,
    list,
    error,
    getGroups,
    changeGroupsFilter,
    resetGroups,
  };
};
